'use client';

import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import type { ComponentProps, ReactNode } from 'react';
import type { DetailedTicket, LotteryName } from '../../../lottery-purchases/types';
import type { DrawnNumbers } from '../../liveDraw/getRevealedNumbers';
import { useOptionalLiveDraw } from '../../liveDraw/LiveDrawProvider';
import type { LiveDrawSlot } from '../../liveDraw/resolveLiveDrawStage';
import { YourNumbersCard } from './YourNumbersCard/YourNumbersCard';
import { YourNumbersEmptyState } from './YourNumbersEmptyState/YourNumbersEmptyState';
import { YourNumbersPod, type YourNumbersDrawnLine } from './YourNumbersPod/YourNumbersPod';

type YourNumbersTicket = Pick<DetailedTicket, 'ticketId' | 'lotteryName' | 'lineInfo'> & {
  /**
   * Per-line prizes and optional explicit matching, keyed by `lineNumber`. During a live reveal,
   * matching is the intersection of each line with `useLiveDraw().revealedNumbers`.
   */
  drawnLines?: Record<number, YourNumbersDrawnLine>;
  /**
   * Set once the draw animation has finished. This is what swaps the footer from the live-draw "Match!" message to the
   * final total, so it must be set even for a losing ticket (where `totalWinnings` is `0`).
   */
  isDrawComplete?: boolean;
  /** Prize tiers are still pending after the draw animation. */
  isCalculating?: boolean;
  /** Total winnings across all lines, in cents. Only rendered once `isDrawComplete` is set. */
  totalWinnings?: number;
};

type YourNumbersSectionProps = ComponentProps<'div'> & {
  tickets: YourNumbersTicket[];
  betUrl: string;
  /** Names the lottery in the empty state, so Lucky for Life does not invite the player into The Daily Million. */
  lotteryName: LotteryName;
  /**
   * Revealed prefix from `useLiveDraw().revealedNumbers`. Stories can pass it directly; on the
   * results page it defaults to the sequencer from the first ball right through to `complete`,
   * where the prefix is the whole draw.
   */
  revealedNumbers?: DrawnNumbers;
  /**
   * True while the drawing's tickets are being fetched, including a stale refetch after a purchase
   * invalidates the cache. The previous slip is still in `tickets` until that response lands.
   */
  isLoading?: boolean;
  /** Renders each slip's total winnings footer once its draw is complete. Defaults to `true`. */
  showTotalWinnings?: boolean;
};

/**
 * Stages where the sequencer's revealed prefix is the source of truth for matching, `complete`
 * included — the prefix is the whole draw there, so matches stay lit on the finished slip instead
 * of vanishing the moment the animation ends.
 *
 * The stages before the first ball count too, with an empty prefix: a replayed drawing's tickets
 * already carry their settled matches, which would otherwise light up over the 3 / 2 / 1.
 */
function doesLiveDrawSupersedeTicketMatching(kind: LiveDrawSlot['kind'] | undefined) {
  return kind === 'draw_starting' || kind === 'countdown_go' || kind === 'drawing_numbers' || kind === 'calculating_win' || kind === 'announce_win' || kind === 'complete';
}

/** Mid-animation stages, where prizes and the total footer have to stay hidden. */
function isLiveDrawInFlight(kind: LiveDrawSlot['kind'] | undefined) {
  return kind === 'drawing_numbers' || kind === 'calculating_win' || kind === 'announce_win';
}

/**
 * Stages where the slip must not claim to be calculating.
 *
 * The board owns that message during `calculating_win`, and during the countdown / reveal the
 * prize is still in flight so the slip would flash "calculating" over the 3 / 2 / 1. It does not
 * gate `isDrawComplete` — a slip that was already finished stays finished while the next
 * countdown plays.
 */
function isLiveDrawCalculatingSuppressed(kind: LiveDrawSlot['kind'] | undefined) {
  return kind === 'countdown_go' || isLiveDrawInFlight(kind);
}

/**
 * The sequence is authoritative while it runs: still playing means not complete, and `complete`
 * means complete even before the ticket has been refetched with its prizes.
 */
function resolveIsDrawComplete(ticketValue: boolean | undefined, kind: LiveDrawSlot['kind'] | undefined) {
  if (isLiveDrawInFlight(kind)) {
    return false;
  }

  return ticketValue ?? (kind === 'complete' ? true : undefined);
}

function resolveYourNumbersContent({
  tickets,
  betUrl,
  lotteryName,
  isLoading,
  revealedNumbers,
  stageKind,
  showTotalWinnings,
}: {
  tickets: YourNumbersTicket[];
  betUrl: string;
  lotteryName: LotteryName;
  isLoading: boolean;
  revealedNumbers: DrawnNumbers | undefined;
  stageKind: LiveDrawSlot['kind'] | undefined;
  showTotalWinnings: boolean;
}): ReactNode {
  if (isLoading) {
    return (
      <YourNumbersCard className="p-4" data-testid="your-numbers-loading">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-12 w-full rounded-full" />
        <Skeleton className="h-12 w-full rounded-full" />
      </YourNumbersCard>
    );
  }

  if (tickets.length === 0) {
    return <YourNumbersEmptyState betUrl={betUrl} lotteryName={lotteryName} />;
  }

  return tickets.map((ticket, index) => {
    const lines = ticket.lineInfo[ticket.lotteryName] ?? [];

    return (
      <YourNumbersPod
        key={ticket.ticketId}
        lines={lines}
        lotteryName={ticket.lotteryName}
        footerMessageIndex={index}
        showTitle={index === 0}
        defaultExpanded={index === 0}
        revealedNumbers={revealedNumbers}
        drawnLines={ticket.drawnLines}
        isDrawComplete={resolveIsDrawComplete(ticket.isDrawComplete, stageKind)}
        isCalculating={isLiveDrawCalculatingSuppressed(stageKind) ? false : ticket.isCalculating}
        totalWinnings={ticket.totalWinnings}
        showTotalWinnings={showTotalWinnings}
      />
    );
  });
}

function YourNumbersSection({
  tickets,
  betUrl,
  lotteryName,
  revealedNumbers: revealedNumbersProp,
  isLoading = false,
  showTotalWinnings = true,
  className,
  ...props
}: YourNumbersSectionProps) {
  const liveDraw = useOptionalLiveDraw();
  const followsSequencer = liveDraw?.isLiveDrawView !== false;
  const stageKind = followsSequencer ? liveDraw?.stage.slot.kind : undefined;
  const isLiveDrawSuperseding = doesLiveDrawSupersedeTicketMatching(stageKind);
  const revealedNumbers = revealedNumbersProp ?? (isLiveDrawSuperseding ? liveDraw?.revealedNumbers : undefined);

  return (
    <div className={twMerge('flex w-full flex-col gap-5', className)} data-testid="your-numbers-section" {...props}>
      {resolveYourNumbersContent({ tickets, betUrl, lotteryName, isLoading, revealedNumbers, stageKind, showTotalWinnings })}
    </div>
  );
}

export { YourNumbersSection, type YourNumbersSectionProps, type YourNumbersTicket };
